import Chart from "./Chart";
import FocusManager from "./FocusManager";
import NodesGenerator from "./FunctionGenerators/NodesGenerator";
import LinksGenerator from "./FunctionGenerators/LinksGenerator";
import SimulationGenerator from "./FunctionGenerators/SimulationGenerator";
import NodeRadiusCreator from "./FunctionGenerators/NodeRadiusCreator";
import ActiveDataManager from "./ActiveDataManager";
import ClustersGenerator from "./Clustering/ClustersGenerator";
import SuperClustersGenerator from "./Clustering/SuperClustersGenerator";
import SharedNodesClustersGenerator from "./Clustering/SharedNodesClustersGenerator";
import ColorGenerator from "./FunctionGenerators/ColorGenerator";
import NodeDispenser_Orbitals from "./NodeDispenser/NodeDispenser_Orbitals";
import CirclesDispenser_CircleContour from "./NodeDispenser/CirclesDispenser_CircleContour";
import HighlightingManager from "./HighlightingManager";
import NodesClassifier from "./Auxiliar/NodesClassifier";

export default class ForceDirectedGraph extends Chart {
    // Public:
    constructor(cParams) {
        super(cParams);
        this.saveParams(cParams);
        this.clearTimeouts();
        this.createHighlightingManager();
        this.attachOnBackgroundClickFunc();
        this._DEBUGGING_FUNCS = [];
    }

    unselectNodes() {
        this._highlightingManager.abortAll();
    }

    selectNode(targetNode) {
        if (targetNode) {
            this._highlightingManager.highlightNode(targetNode);
        }
    }

    setGravitationalCenter(center) {
        let zoomManager = this._zoomManager;
        let simulationGen = this._simulationGen;
        if (simulationGen) simulationGen.setGravitationalCenter(center);
        if (zoomManager) zoomManager.setCenter(center);
    }

    applyAutoFit() {
        let nodes = this._activeNodes;
        let zoomManager = this._zoomManager;
        zoomManager.applyAutoFit(nodes);
    }

    applyInitialAutoFit() {
        let nodes = this._activeNodes;
        let zoomManager = this._zoomManager;
        zoomManager.applyInitialAutoFit(nodes);
    }

    getActiveNodes() {
        return this._activeNodes;
    }

    // Protected:
    compute() {
        this.saveInputData();
        this.classifyNodes();
        this.getNodesIdList();
        this.createNodesCoordinates();
        this.setNodesRadius();
        this.createActiveDataManager();
        this.computeActiveData();
        this.computeClustering();
        this.setLinksCategories();
        this.setLinksColors();
        this.setNodesColors();
        this.initNodesPosition();
        this.createFocusManager();
        this.applyInitialAutoFit();
        this.createSimulationGenerator();
    }

    draw() {
        this.createLinkFunction();
        this.createNodeFunction();
        // this.DEBUGGING_FUNCTIONS();
        this.createSimulationFunction();
        this.setupFocus();
        this.setupHighlighting();
        this.attachFadeLabelsFunctionOnZoomChange();
    }

    // Private:
    saveParams(cParams) {
        this._nodesSettings = cParams.nodesSettings;
        this._linksSettings = cParams.linksSettings;
        this._textSettings = cParams.textSettings;
        this._simulationSettings = cParams.simulationSettings;
        this._highlightingSettings = cParams.highlightingSettings;
        this._autoFitSettings = cParams.autoFitSettings;
        this._clusteringSettings = cParams.clusteringSettings;
        this._minZoomScale = cParams.minZoomScale;
        this._maxNodesPerSpeaker = cParams.maxNodesPerSpeaker;
        this._nodeLocationFactor = cParams.nodeLocationFactor;
        this._nodesOnclickFunc = cParams.nodesOnclickFunc;
        this._backgroundOnclickFunc = cParams.backgroundOnclickFunc;
    }

    clearTimeouts() {
        const elementId = this._elementId;
        const element = document.getElementById(elementId);
        element.deleteAllTimeouts();
    }

    createHighlightingManager() {
        let cParams = {
            nodesSettings: {
                highValue: this._nodesSettings.opacity,
                lowValue: this._nodesSettings.opacityFaded,
            },
            linksSettings: {
                highValue: this._linksSettings.opacity,
                lowValue: this._linksSettings.opacityFaded,
            },
            textSettings: {
                highValue: this._textSettings.opacity,
                lowValue: this._textSettings.opacityFaded,
            },
            highlightingSettings: this._highlightingSettings,
            elementId: this._elementId,
        };
        let highlightingManager = new HighlightingManager(cParams);
        this._highlightingManager = highlightingManager;
    }

    setupHighlighting() {
        let highlightingManager = this._highlightingManager;
        highlightingManager.compute();
    }

    attachOnBackgroundClickFunc() {
        const func = e => {
            if (e.target.localName !== "circle") this._backgroundOnclickFunc();
        };
        this._chartElement.onclick = func;
    }

    createFocusManager() {
        let cParams = {
            activeDataManager: this._activeDataManager,
            inputNodes: this._inputNodes,
            inputLinks: this._inputLinks,
            clusters: this._clusters,
            chart: this,
        };
        let focusManager = new FocusManager(cParams);
        this._focusManager = focusManager;
    }

    setupFocus() {
        let focusManager = this._focusManager;
        focusManager.compute();
    }

    createNodesCoordinates() {
        const elementId = this._elementId;
        const element = document.getElementById(elementId);
        let nodes = this._inputNodes;
        nodes.forEach(node => {
            node.x = element.clientWidth / 2;
            node.y = element.clientHeight / 2;
        });
    }

    setNodesRadius() {
        let cParams = {
            nodes: this._inputNodes,
            nodesSettings: this._nodesSettings,
        };
        let nodeRadiusCreator = new NodeRadiusCreator(cParams);
        nodeRadiusCreator.compute();
    }

    createActiveDataManager() {
        const cParams = {
            inputNodes: this._inputNodes,
            inputLinks: this._inputLinks,
            maxNodesPerSpeaker: this._maxNodesPerSpeaker,
            chart: this,
        };
        let activeDataManager = new ActiveDataManager(cParams);
        this._activeDataManager = activeDataManager;
    }

    computeActiveData() {
        let activeDataManager = this._activeDataManager;
        activeDataManager.compute();
        activeDataManager.setActiveData();
    }

    createLinkFunction() {
        this.createLinksGenerator();
        let links = this._activeLinks;
        this._linkFunc = this._linksGen.compute(links);
    }

    createNodeFunction() {
        this.createNodesGenerator();
        let nodes = this._activeNodes;
        this._nodeFunc = this._nodesGen.compute(nodes);
    }

    computeClustering() {
        this.computeClusters();
        this.computeSuperClusters();
        this.computeSharedNodesClusters();
    }

    computeClusters() {
        let cParams = this._clusteringSettings;
        cParams.inputNodes = this._inputNodes;
        cParams.activeNodes = this._activeNodes;
        let clustersGenerator = new ClustersGenerator(cParams);
        this._clusters = clustersGenerator.compute();
    }

    computeSuperClusters() {
        let clusters = this._clusters;
        let superClusterGen = new SuperClustersGenerator(clusters);
        let superClusters = superClusterGen.compute();
        this._superClusters = superClusters;
    }

    computeSharedNodesClusters() {
        let cParams = {
            clusters: this._clusters,
            nodes: this._inputNodes,
        };
        let sharedNodesClusterGen = new SharedNodesClustersGenerator(cParams);
        let sharedNodesClusters = sharedNodesClusterGen.compute();
        this._sharedNodesClusters = sharedNodesClusters;
    }

    setLinksCategories() {
        let links = this._activeLinks;
        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            const target = link.target;
            const category = target.category;
            link.category = category;
        }
    }

    setLinksColors() {
        let cParams = {
            color: this._linksSettings.color,
        };
        let colorGen = new ColorGenerator(cParams);
        let func = colorGen.compute();
        let links = this._inputLinks;
        links.forEach(link => {
            const group = link.group;
            link.color = func(group);
        });
    }

    setNodesColors() {
        let cParams = {
            color: this._nodesSettings.color,
        };
        let colorGen = new ColorGenerator(cParams);
        let func = colorGen.compute();
        let nodes = this._inputNodes;
        nodes.forEach(node => {
            const group = node.group;
            node.color = func(group);
        });
    }

    createSimulationFunction() {
        let cParams = {
            activeNodes: this._activeNodes,
            activeLinks: this._activeLinks,
            inputNodes: this._inputNodes,
            inputLinks: this._inputLinks,
            nodeFunc: this._nodeFunc,
            linkFunc: this._linkFunc,
            clusters: this._clusters,
            superClusters: this._superClusters,
            sharedNodesClusters: this._sharedNodesClusters,
            DEBUGGING_FUNCS: this._DEBUGGING_FUNCS,
        };
        this._simulationFunc = this._simulationGen.compute(cParams);
    }

    initNodesPosition() {
        this.setNodesPositionInClusters();
        this.setClustersPositionInSuperclusters();
        this.setSuperclustersPosition();
    }

    setNodesPositionInClusters() {
        let nodes = this._activeNodes;
        let nodeDispenser = new NodeDispenser_Orbitals();
        nodeDispenser.compute(nodes);
    }

    setClustersPositionInSuperclusters() {
        let superClusters = this._superClusters;
        for (let i = 0; i < superClusters.length; i++) {
            const superCluster = superClusters[i];
            let clusters = superCluster.clusters;
            let cParams = {
                circles: clusters,
                factor: this._nodeLocationFactor,
            };
            let clusterDispenser = new CirclesDispenser_CircleContour(cParams);
            clusterDispenser.compute();
        }
    }

    setSuperclustersPosition() {
        let superClusters = this._superClusters;
        let cParams = {
            circles: superClusters,
            factor: this._nodeLocationFactor,
        };
        let superClusterDispenser = new CirclesDispenser_CircleContour(cParams);
        superClusterDispenser.compute();
    }

    attachFadeLabelsFunctionOnZoomChange() {
        const func = this._nodesGen.getLabelFadingFunction();
        this._zoomManager.addOnZoomChangeListener(func);
    }

    createNodesGenerator() {
        let cParams = this._nodesSettings;
        cParams.elementId = this._elementId;
        cParams.textSettings = this._textSettings;
        cParams.svg = this._svg;
        cParams.dragFunc = this._simulationGen._dragFunc;
        cParams.onclickFunc = this._nodesOnclickFunc;
        this._nodesGen = new NodesGenerator(cParams);
    }

    createLinksGenerator() {
        let cParams = this._linksSettings;
        cParams.svg = this._svg;
        this._linksGen = new LinksGenerator(cParams);
    }

    createSimulationGenerator() {
        let cParams = this._simulationSettings;
        cParams.elementId = this._elementId;
        cParams.textSettings = this._textSettings;
        this._simulationGen = new SimulationGenerator(cParams);
    }

    getNodesIdList() {
        const list = [];
        const nodes = this._inputNodes;
        nodes.forEach(node => {
            const id = node.id;
            list.push(id);
        });
        this._nodesIds = list;
    }

    saveInputData() {
        this._inputNodes = this._inputData.nodes;
        this._inputLinks = this._inputData.links;
    }

    classifyNodes() {
        const cParams = {
            nodes: this._inputNodes,
            links: this._inputLinks,
        };
        let classifier = new NodesClassifier(cParams);
        classifier.compute();
    }

    DEBUGGING_FUNCTIONS() {
        this.DEBUGGING_DRAW_CLUSTERS_RADII();
        this.DEBUGGING_DRAW_SUPERCLUSTERS_RADII();
        this.DEBUGGING_DRAW_SHAREDNODESCLUSTERS_RADII();
        this.DEBUGGING_DRAW_SCREEN_DELIMITERS();
    }

    DEBUGGING_DRAW_CLUSTERS_RADII() {
        this.DEBUGGING_DRAW_CLUSTER_EXTERNAL_RADIUS();
        this.DEBUGGING_DRAW_CLUSTER_SOLO_RADIUS();
        this.DEBUGGING_DRAW_CLUSTER_CLUSTER_RADIUS();
        this.DEBUGGING_DRAW_CLUSTER_SHARED_RADIUS();
    }

    DEBUGGING_DRAW_CLUSTER_EXTERNAL_RADIUS() {
        let svg = this._svg;
        let clusters = this._clusters;
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(clusters)
            .enter()
            .append("g");

        func.radiusFunc = d => d.radius;
        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "blue");
        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_DRAW_CLUSTER_SOLO_RADIUS() {
        let svg = this._svg;
        let clusters = this._clusters;
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(clusters)
            .enter()
            .append("g");

        func.radiusFunc = d => d.soloRadius;
        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "green");
        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_DRAW_CLUSTER_CLUSTER_RADIUS() {
        let svg = this._svg;
        let clusters = this._clusters;
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(clusters)
            .enter()
            .append("g");

        func.radiusFunc = d => d.clusterRadius;
        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "purple");
        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_DRAW_CLUSTER_SHARED_RADIUS() {
        let svg = this._svg;
        let clusters = this._clusters;
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(clusters)
            .enter()
            .append("g");

        func.radiusFunc = d => d.sharedRadius;
        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "yellow");
        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_DRAW_SUPERCLUSTERS_RADII() {
        let svg = this._svg;
        let superClusters = this._superClusters;
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(superClusters)
            .enter()
            .append("g");

        func.radiusFunc = d => d.radius;

        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "red");

        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_DRAW_SHAREDNODESCLUSTERS_RADII() {
        let svg = this._svg;
        let sharedClusters = this._sharedNodesClusters;
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data(sharedClusters)
            .enter()
            .append("g");

        func.radiusFunc = d => d.sharedRadius;
        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "brown");
        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_DRAW_SCREEN_DELIMITERS() {
        let svg = this._svg;
        let w = window.innerWidth;
        let h = window.innerHeight;
        let P0 = {
            x: 0,
            y: 0,
        };
        let P1 = {
            x: w,
            y: h,
        };
        let func = svg
            .append("g")
            .attr("class", "nodes")
            .selectAll("g")
            .data([P0, P1])
            .enter()
            .append("g");

        func.radiusFunc = () => 10;
        func = this.DEBUGGING_APPEND_CIRCLE_FUNC(func, "black");
        this._DEBUGGING_FUNCS.push(func);
    }

    DEBUGGING_APPEND_CIRCLE_FUNC(func, color) {
        let radiusFunc = func.radiusFunc;
        func.append("circle")
            .attr("r", radiusFunc)
            .attr("cx", d => d.x)
            .attr("cy", d => d.y)
            .style("fill", "none")
            .style("stroke", color);

        return func;
    }
}
